import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, Send, Clock, CheckCircle, AlertTriangle, RefreshCw,
  Eye, Users, User, Briefcase, Calendar
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const ConfigNotificaciones = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReports, setSendingReports] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState('partner');
  const [previewContent, setPreviewContent] = useState('');
  const [previewArea, setPreviewArea] = useState('Tributaria');

  useEffect(() => {
    fetchSchedulerStatus();
  }, []);

  const fetchSchedulerStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/scheduler/status');
      setSchedulerStatus(response.data);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerReports = async () => {
    if (!confirm('¿Enviar los reportes semanales a TODOS los usuarios ahora?')) return;
    
    setSendingReports(true);
    try {
      const response = await api.post('/scheduler/trigger-weekly-reports');
      toast.success(
        `Reportes enviados: ${response.data.results.partners_sent} socios, ` +
        `${response.data.results.leaders_sent} líderes, ` +
        `${response.data.results.employees_sent} empleados`
      );
      fetchSchedulerStatus();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSendingReports(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Ingrese un email de destino');
      return;
    }
    
    setSendingTest(true);
    try {
      await api.post(`/email/test?recipient_email=${encodeURIComponent(testEmail)}`);
      toast.success(`Email de prueba enviado a ${testEmail}`);
      setTestEmail('');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreviewEmail = async (type, area = null) => {
    setPreviewType(type);
    setShowPreview(true);
    
    try {
      let url = '/email/preview/';
      if (type === 'partner') url += 'partner';
      else if (type === 'leader') url += `leader/${area || previewArea}`;
      else url += 'employee';
      
      const response = await api.get(url, { responseType: 'text' });
      setPreviewContent(response.data);
    } catch (error) {
      toast.error(formatErrorMessage(error));
      setShowPreview(false);
    }
  };

  const formatNextRun = (isoDate) => {
    if (!isoDate) return 'No programado';
    const date = new Date(isoDate);
    return date.toLocaleString('es-CL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-10 h-10 text-primary" strokeWidth={1.5} />
                <h1 className="text-4xl font-bold text-slate-900" data-testid="notifications-title">
                  Configuración de Notificaciones
                </h1>
              </div>
              <p className="text-lg text-slate-600">
                Gestión de emails automáticos y reportes semanales
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchSchedulerStatus}
              disabled={loading}
              data-testid="refresh-status-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar Estado
            </Button>
          </div>

          {/* Scheduler Status Card */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Estado del Programador
              </h2>
              {schedulerStatus?.status === 'running' ? (
                <span className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Activo
                </span>
              ) : (
                <span className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1 rounded-full text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  Inactivo
                </span>
              )}
            </div>

            {schedulerStatus?.jobs?.map((job, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-4 mb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{job.name}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Programación: {job.trigger}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Próximo envío:</p>
                    <p className="font-medium text-primary">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {formatNextRun(job.next_run)}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex gap-3 mt-4">
              <Button
                onClick={handleTriggerReports}
                disabled={sendingReports}
                className="bg-primary hover:bg-primary/90"
                data-testid="trigger-reports-btn"
              >
                {sendingReports ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Enviar Reportes Ahora
              </Button>
            </div>
          </Card>

          {/* Tabs for different sections */}
          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="preview" className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Vista Previa
              </TabsTrigger>
              <TabsTrigger value="test" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email de Prueba
              </TabsTrigger>
            </TabsList>

            <TabsContent value="preview">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Vista Previa de Reportes
                </h3>
                <p className="text-slate-600 mb-6">
                  Visualiza cómo se verán los emails antes de enviarlos.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Partner Report Preview */}
                  <Card 
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary/50"
                    onClick={() => handlePreviewEmail('partner')}
                    data-testid="preview-partner-btn"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Briefcase className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Reporte Ejecutivo</p>
                        <p className="text-sm text-slate-500">Para Socios</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">
                      KPIs globales, carga de trabajo, métricas por área.
                    </p>
                    <Button variant="outline" className="w-full mt-3" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Vista Previa
                    </Button>
                  </Card>

                  {/* Leader Report Preview */}
                  <Card 
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary/50"
                    onClick={() => handlePreviewEmail('leader', 'Tributaria')}
                    data-testid="preview-leader-btn"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Estado del Equipo</p>
                        <p className="text-sm text-slate-500">Para Líderes</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">
                      Tareas del equipo, pendientes de revisión, carga por miembro.
                    </p>
                    <Button variant="outline" className="w-full mt-3" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Vista Previa
                    </Button>
                  </Card>

                  {/* Employee Report Preview */}
                  <Card 
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary/50"
                    onClick={() => handlePreviewEmail('employee')}
                    data-testid="preview-employee-btn"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <User className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Mis Tareas</p>
                        <p className="text-sm text-slate-500">Para Empleados</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">
                      Lista de tareas pendientes, devueltas y próximos vencimientos.
                    </p>
                    <Button variant="outline" className="w-full mt-3" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Vista Previa
                    </Button>
                  </Card>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="test">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Enviar Email de Prueba
                </h3>
                <p className="text-slate-600 mb-6">
                  Verifica que la configuración de email esté funcionando correctamente.
                </p>

                <div className="max-w-md">
                  <Label htmlFor="test-email">Email de Destino</Label>
                  <div className="flex gap-3 mt-2">
                    <Input
                      id="test-email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      data-testid="test-email-input"
                    />
                    <Button
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !testEmail}
                      data-testid="send-test-btn"
                    >
                      {sendingTest ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Se enviará un email de prueba simple al correo especificado.
                  </p>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Info Cards - Horarios de Envío */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card className="p-4 bg-gradient-to-br from-green-50 to-white border-green-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-900">Funcionarios</p>
                  <p className="text-sm text-green-600">Lunes 09:00 AM</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Áreas: Legal, Tributaria, Contabilidad, Comercial, Adm. y Finanzas</p>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-blue-50 to-white border-blue-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-blue-900">Líderes de Área</p>
                  <p className="text-sm text-blue-600">Lunes 09:00 AM</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Reporte de desempeño y tareas "Por Revisar"</p>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-red-50 to-white border-red-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-red-900">Socios / Partners</p>
                  <p className="text-sm text-red-600">Lunes 09:30 AM</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Dashboard de KPIs y estado general</p>
            </Card>
          </div>
        </motion.div>

        {/* Email Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                Vista Previa: {
                  previewType === 'partner' ? 'Reporte Ejecutivo (Socios)' :
                  previewType === 'leader' ? 'Estado del Equipo (Líderes)' :
                  'Mis Tareas (Empleados)'
                }
              </DialogTitle>
              <DialogDescription>
                Así se verá el email que recibirán los usuarios.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 overflow-auto max-h-[70vh] border rounded-lg">
              {previewContent ? (
                <iframe
                  srcDoc={previewContent}
                  className="w-full min-h-[600px] border-0"
                  title="Email Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ConfigNotificaciones;
